
import { CommonModule } from '@angular/common';
import { Component, DoCheck, ElementRef, HostListener, input, OnDestroy, TemplateRef } from '@angular/core';
import { panelRight } from 'src/app/animations';
import { SliderService } from './slider.service';

@Component({
  selector: 'app-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  standalone: true,
  animations: [panelRight],
  imports: [CommonModule]
})
export class SliderComponent implements DoCheck, OnDestroy {
  private static readonly FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  private static idSequence = 0;
  /** Id du titre, référencé par `aria-labelledby` du panneau. */
  readonly titleId = `app-slider-title-${++SliderComponent.idSequence}`;
  private etaitOuvert = false;
  private focusAvantOuverture: HTMLElement | null = null;

  readonly customContent = input<TemplateRef<unknown> | null>(null);
  readonly footerContent = input<TemplateRef<unknown> | null>(null);
  readonly headerContent = input<TemplateRef<unknown> | null>(null);
  readonly title = input('');
  readonly subtitle = input<string | undefined>(undefined);
  readonly width = input('small');
  readonly type = input.required<string>();
  readonly titleSize = input<string | undefined>(undefined);
  readonly showBackArrow = input(true);
  readonly footerBorder = input(true);
  readonly embedded = input(false);
  /** En mode embedded, fait passer le panneau en overlay plein écran (au-dessus de tout le contenu de la page hôte) au lieu d'un panneau latéral contraint en largeur. */
  readonly fullPage = input(false);
  readonly embeddedWidthCss = input<string | null>(null);
  readonly embedBodyScrollInChild = input(false);
  readonly afterSliderClose = input<(() => void) | undefined>(undefined);
  readonly headerBorder = input(true);
  readonly padding = input(true);

  constructor(
    public sliderService: SliderService,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  get embeddedPanelNgClass(): string[] | null {
    if (!this.embedded()) {
      return null;
    }
    const widthCss = this.embeddedWidthCss()?.trim();
    const classes = widthCss
      ? ['panel-right--embedded', 'panel-right--embedded--custom']
      : ['panel-right--embedded', `panel-right--embedded--${this.width()}`];
    if (this.fullPage()) {
      // Garde la largeur ci-dessus : seule la position change (panneau qui remonte
      // au-dessus du contenu de la page hôte au lieu d'être confiné dans son conteneur).
      classes.push('panel-right--embedded-fullpage');
    }
    return classes;
  }

  get embeddedCustomWidthStyle(): string | null {
    const widthCss = this.embeddedWidthCss()?.trim();
    return this.embedded() && widthCss ? widthCss : null;
  }

  /**
   * Volet « classique » (avec fond gris) : modal au sens RGAA (role="dialog", aria-modal, Échap,
   * focus géré). Les volets embedded (panneaux latéraux de l'éditeur d'automatisations) laissent
   * volontairement la page utilisable à côté : simple region titrée.
   */
  public isModal(): boolean {
    return !this.embedded();
  }

  /** Molette / glisser tactile sur le fond gris : ne doit pas faire défiler la page derrière. */
  public bloquerDefilementFond(event: Event): void {
    event.preventDefault();
  }

  public close(): void {
    this.sliderService.closeSlider(this.type());
    this.afterSliderClose()?.();
  }

  /**
   * Empêche Tab de faire défiler la page derrière le panneau (scrollIntoView
   * du navigateur sur un focus dans un panneau position:fixed) et garde le focus
   * dans le slider.
   */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || !this.sliderService.isSliderOpen(this.type())) {
      return;
    }
    const focusables = this.getFocusableElements();
    if (focusables.length === 0) {
      return;
    }
    event.preventDefault();
    const active = document.activeElement as HTMLElement | null;
    const currentIndex = active ? focusables.indexOf(active) : -1;
    const nextIndex = event.shiftKey
      ? (currentIndex <= 0 ? focusables.length - 1 : currentIndex - 1)
      : (currentIndex === -1 || currentIndex >= focusables.length - 1 ? 0 : currentIndex + 1);
    focusables[nextIndex].focus({ preventScroll: true });
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    // Modal ou intégré (embedded) : Échap ferme le volet, comme la flèche Retour.
    if (!this.sliderService.isSliderOpen(this.type()) || event.defaultPrevented) {
      return;
    }
    // Échap ferme d'abord une liste déroulante / un calendrier ouvert, pas le volet.
    const active = document.activeElement as HTMLElement | null;
    if (active?.getAttribute('aria-expanded') === 'true' || document.querySelector('.e-popup.e-popup-open')) {
      return;
    }
    // Plusieurs volets ouverts : seul celui qui contient le focus réagit.
    const container = this.getContainer();
    const autreVolet = active?.closest('.panel-right_container');
    if (autreVolet && autreVolet !== container) {
      return;
    }
    event.preventDefault();
    this.close();
  }

  /**
   * Détecte l'ouverture / la fermeture (l'état vit dans SliderService, sans signal) pour placer
   * le focus dans le volet à l'ouverture et le rendre à l'élément d'origine à la fermeture
   * (seulement s'il a été perdu : passer d'un volet intégré à un autre ne le déplace pas).
   */
  ngDoCheck(): void {
    const ouvert = this.sliderService.isSliderOpen(this.type());
    if (ouvert === this.etaitOuvert) {
      return;
    }
    this.etaitOuvert = ouvert;
    // Volets modaux comme intégrés (embedded) : un volet ouvert au clavier doit pouvoir être parcouru puis quitté.
    if (ouvert) {
      this.focusAvantOuverture = document.activeElement as HTMLElement | null;
      setTimeout(() => this.placerFocusDansVolet());
    } else {
      const aRendre = this.focusAvantOuverture;
      this.focusAvantOuverture = null;
      setTimeout(() => {
        const focusPerdu = !document.activeElement || document.activeElement === document.body;
        if (focusPerdu && aRendre?.isConnected) {
          aRendre.focus({ preventScroll: true });
        }
      });
    }
  }

  private placerFocusDansVolet(): void {
    const container = this.getContainer();
    // Un écran qui place lui-même le focus dans son volet (champ précis) n'est pas écrasé.
    if (!container || container.contains(document.activeElement)) {
      return;
    }
    const premier = this.getFocusableElements()[0];
    (premier ?? container).focus({ preventScroll: true });
  }

  private getContainer(): HTMLElement | null {
    return this.elementRef.nativeElement.querySelector('.panel-right_container') as HTMLElement | null;
  }

  private getFocusableElements(): HTMLElement[] {
    const container = this.getContainer();
    if (!container) {
      return [];
    }
    return Array.from(
      container.querySelectorAll<HTMLElement>(SliderComponent.FOCUSABLE_SELECTOR),
    ).filter((el) => {
      const style = window.getComputedStyle(el);
      return style.visibility !== 'hidden' && style.display !== 'none';
    });
  }

  ngOnDestroy() {
    this.sliderService.closeSlider(this.type());
  }
}
